import { SENDA } from '@somnhold/content';
import { DIRS, hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The cut bridge's grounds (docs/sendas/guerrero.md, chapter 2): a gorge between two plates of the shell, the Furrows
 * far below. Each act stands on a piece of it; what can't be walked is the drop (the map's rocks, drawn as the void by
 * the renderer's ChasmLayer). The same from the seed in the sim and the drawing, integers only.
 *
 * - `rimLayout` (act 1, the hooks): this side's edge, the drop to the north (−y), its line wavering a row up or down
 *   from the seed; the old bridge's posts in the middle with Kel's winches behind them; the anchors where the hooks
 *   bite, spread along the edge either side of the posts.
 * - `spanLayout` (act 2, the windbreak): the rock platform in the middle of the gorge, long from south (+y, where the
 *   raised span lands) to north, the drop either side (the wind runs along the gorge, across the platform); Ari and
 *   Gerd kneeling on one side of its middle, covering it.
 * - `weightsLayout` (act 3, the counterweight): the platform's north end, the drop beyond it and to both sides; the
 *   three pans along its lip (left, middle, right), the pile of pieces behind them.
 */

/** A cell's column across (half-hex steps, floored to whole hexes): its world x in hexes. */
export const chasmCol = (q: number, r: number): number => (2 * q + r) >> 1;

export interface ChasmGround {
  /** The clearing's radius (the map's is two more). */
  radius: number;
  /** The drop: every cell of the map that can't be walked. */
  drop: Hex[];
}

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

export interface RimLayout extends ChasmGround {
  /** The edge's row in each column (−R..R, index + R): cells above it (smaller r) are the drop. */
  edge: number[];
  /** Where the hooks bite (rim cells, west to east). */
  anchors: Hex[];
  /** The old bridge's two posts at the edge, the hinge of the span between them, and where Kel winds. */
  posts: [Hex, Hex];
  winch: Hex;
  /** Wenn and her mule, at the wood's edge out of the fight. */
  mule: Hex;
}

export const RIM_RADIUS = 7;

/** This side's edge (act 1): its line from the seed, the posts and the winches in the middle, the anchors along it. */
export const rimLayout = (seed: number): RimLayout => {
  const R = RIM_RADIUS;
  const M = R + 2;
  const rng = seedRng(deriveSeed(seed, 0x51a3));
  // The edge's row by column: −3 by the posts, wandering a row at a time outward (never two in one step).
  const edge: number[] = new Array(2 * M + 1).fill(-3);
  for (const dir of [1, -1]) {
    let row = -3;
    for (let c = 2; c <= M; c++) {
      const step = rngInt(rng, 5);
      if (step === 0 && row > -4) row--;
      else if (step === 4 && row < -2) row++;
      edge[M + dir * c] = row;
    }
  }
  const drop: Hex[] = [];
  const land: Hex[] = [];
  for (const h of allCells(M)) {
    const c = chasmCol(h.q, h.r);
    if (h.r < (edge[M + c] as number)) drop.push(h);
    else land.push(h);
  }
  const isDrop = new Set(drop.map((h) => `${h.q},${h.r}`));
  // The rim: land with the drop on its north side, inside the clearing, off the posts' columns.
  const rim = land
    .filter((h) => hexLen(h.q, h.r) <= R - 1 && Math.abs(chasmCol(h.q, h.r)) >= 2 && [DIRS[1], DIRS[2]].some((d) => isDrop.has(`${h.q + (d as Hex).q},${h.r + (d as Hex).r}`)))
    .sort((a, b) => chasmCol(a.q, a.r) - chasmCol(b.q, b.r) || a.r - b.r);
  const n = SENDA.hooks.anchors;
  const anchors: Hex[] = [];
  for (let i = 0; i < n && rim.length; i++) {
    const h = rim[idiv(i * (rim.length - 1) + idiv(n - 1, 2), n - 1)] as Hex;
    if (!anchors.some((a) => a.q === h.q && a.r === h.r)) anchors.push(h);
  }
  return { radius: R, drop, edge, anchors, posts: [{ q: 0, r: -3 }, { q: 3, r: -3 }], winch: { q: 1, r: -2 }, mule: { q: -5, r: 5 } };
};

export interface SpanLayout extends ChasmGround {
  /** The platform's half-width by row (hexes ×2, index r + M), its middle line's x by row (half-hexes). */
  half: number[];
  /** Kel's way: from the south end to the north. */
  from: Hex;
  to: Hex;
  /** Ari and Gerd kneeling, side by side down one flank of the middle; the side (−1 west, +1 east) they cover from. */
  fixed: [Hex, Hex];
  side: number;
}

export const SPAN_RADIUS = 11;

/** The platform (act 2): five to six hexes across, a little crooked from the seed; Ari and Gerd on one flank. */
export const spanLayout = (seed: number): SpanLayout => {
  const R = SPAN_RADIUS;
  const M = R + 2;
  const rng = seedRng(deriveSeed(seed, 0x5a7e));
  // The platform's half-width in half-hexes by row: 5 (two and a half hexes) mostly, 6 here and there.
  const half: number[] = [];
  for (let r = -M; r <= M; r++) half.push(rngInt(rng, 4) === 0 ? 6 : 5);
  const drop: Hex[] = [];
  for (const h of allCells(M)) {
    const x2 = 2 * h.q + h.r;
    const end = h.r < -(R - 1) || h.r > R - 1;
    if (end || Math.abs(x2) > (half[h.r + M] as number)) drop.push(h);
  }
  const side = rngInt(rng, 2) === 0 ? -1 : 1;
  // On the flank the wind comes from, a hex and a half out from the way, one row above the middle and one below.
  const at = (r: number): Hex => ({ q: idiv(side * 3 - r, 2), r });
  const r0 = R - 1;
  return { radius: R, drop, half, from: { q: -(r0 >> 1), r: r0 }, to: { q: r0 >> 1, r: -r0 }, fixed: [at(-1), at(1)], side };
};

export interface WeightsLayout extends ChasmGround {
  /** The three pans along the north lip (left, middle, right), the pile of pieces, where Kel waits by it. */
  pans: [Hex, Hex, Hex];
  pile: Hex;
  kel: Hex;
}

export const WEIGHTS_RADIUS = 7;

/** The platform's north end (act 3): the lip at row −3, the drop beyond and past five hexes either side. */
export const weightsLayout = (): WeightsLayout => {
  const R = WEIGHTS_RADIUS;
  const M = R + 2;
  const drop: Hex[] = [];
  for (const h of allCells(M)) {
    const x2 = 2 * h.q + h.r;
    if (h.r < -3 || Math.abs(x2) > 11) drop.push(h);
  }
  // Row −3: x = q − 1.5; the pans three hexes apart, the middle one at x = 0.5 nearest the middle.
  return { radius: R, drop, pans: [{ q: -1, r: -3 }, { q: 2, r: -3 }, { q: 5, r: -3 }], pile: { q: 0, r: 2 }, kel: { q: -1, r: 2 } };
};

/** A layout's drop as world points' test (for the drawing): is this cell the drop. */
export const dropSet = (g: ChasmGround): Set<string> => new Set(g.drop.map((h) => `${h.q},${h.r}`));

/** A hex's centre in world units (re-exported for the layouts' users). */
export const chasmAt = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);
